import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { UserUtils } from '@app/containers/components/Notifications/utils';
import { Colors } from '@blueprintjs/core';
import React from 'react';
import NotificationTemplate from '../../../../NotificationTemplate';
import {
    NotificationResource,
    TaskDeadlineUpdatedActivityPayloadResource,
    TaskResource,
} from 'dy-frontend-http-repository/lib/modules/Notification/resources';
import { Endpoints } from '@app/data/consts';
import moment from 'moment';

export interface TaskDeadlineUpdatedNotificationProps {
    authorizedUserId: ID;
    notification: NotificationResource;
    task: TaskResource | null;
}

type Props = TaskDeadlineUpdatedNotificationProps;

const TaskDeadlineUpdatedNotification: React.FC<Props> = ({ authorizedUserId, notification, task }) => {
    const renderDeadlineIntervalLabel = (payload: TaskDeadlineUpdatedActivityPayloadResource) => {
        const { to, from } = payload;
        /**
         * 1 case:
         * from = null;
         * to = date;
         * set deadline to ${moment} for
         * 2 case:
         * from = date;
         * to = date;
         * changed deadline from ${moment} to ${moment} for
         */

        if (from === null && to !== null) {
            // Before update deadline was NOT set to anything
            return (
                <DevText inline>
                    set deadline to{' '}
                    <DevText inline muted>
                        {moment(to).format('D MMM HH:mm')}
                    </DevText>{' '}
                    for
                </DevText>
            );
        } else if (from !== null && to !== null) {
            // Updating deadline with new date
            return (
                <DevText inline>
                    changed deadline from {moment(from).format('D MMM, YYYY, HH:mm')} to{' '}
                    {moment(to).format('D MMM, YYYY, HH:mm')} for
                </DevText>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (!task) {
            // This is bad, task is NOT found, which should not happen
            return <DevText color={Colors.RED2}>Request was not found</DevText>;
        }

        const payload: TaskDeadlineUpdatedActivityPayloadResource | null = notification.activity.payload;
        if (!payload) {
            // This is bad, there should be payload, but for some reason it is not there
            return <DevText color={Colors.RED2}>Payload was not found</DevText>;
        }

        if (payload.to === null && payload.from === null) {
            // There were no deadline set and after update it is not set anymore, this doesn't make sense
            return <DevText color={Colors.RED2}>Invalid payload</DevText>;
        }

        return (
            <div>
                <DevText running className="mb-1">
                    {UserUtils.getActorUserName(notification.activity.actor_user, authorizedUserId)}{' '}
                    {renderDeadlineIntervalLabel(payload)}{' '}
                    <RouterLink
                        style={{ display: 'inline' }}
                        to={Endpoints.TASK_INFORMATION.replace(':taskId', task.id)}
                    >
                        {task.title}
                    </RouterLink>
                </DevText>
            </div>
        );
    };

    return (
        <NotificationTemplate
            title="Deadline updated"
            icon="timeline-events"
            at={notification.activity.at}
            content={renderContent()}
        />
    );
};

export default TaskDeadlineUpdatedNotification;
